/**
 * MODULE: assistantFormRequestUtils.ts
 * PURPOSE: Detect assistant form_catalog entities that should use golden-path UX.
 */

import type { EmailTemplateCreatePrefill } from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import type { FormRequest } from '../../hooks/assistant/types';

/** Email template create via form_catalog → Sales Hub editor (not legacy presentation_templates). */
export function isEmailTemplateFormCatalogRequest(formRequest: FormRequest): boolean {
  const entity = (formRequest.entity || formRequest.entityType || '').trim().toLowerCase();
  const action = (formRequest.action || '').trim().toLowerCase();
  const mode = (formRequest.mode || '').trim().toLowerCase();
  if (entity !== 'email_template') return false;
  if (mode === 'edit' || action === 'update') return false;
  return true;
}

/** Email template edit via form_catalog → golden-path editor (not inline FormCard PATCH). */
export function isEmailTemplateEditFormCatalogRequest(formRequest: FormRequest): boolean {
  const entity = (formRequest.entity || formRequest.entityType || '').trim().toLowerCase();
  if (entity !== 'email_template') return false;
  const mode = (formRequest.mode || formRequest.continuation?.mode || '').trim().toLowerCase();
  const action = (formRequest.action || '').trim().toLowerCase();
  return mode === 'edit' || action === 'update';
}

export function extractEmailTemplateIdFromFormRequest(formRequest: FormRequest): string | null {
  const raw =
    formRequest.recordId ||
    formRequest.continuation?.record_id ||
    formRequest.continuation?.recordId;
  if (typeof raw !== 'string') return null;
  const id = raw.trim();
  return id || null;
}

/** Map form_catalog fields into quota modal prefill. */
export function extractSalesTargetPrefillFromFormRequest(
  formRequest: FormRequest
): {
  targetAmount?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
} {
  const collected = formRequest.continuation?.collected_fields ?? {};
  const fieldDefault = (name: string): string => {
    const fromCollected = collected[name];
    if (typeof fromCollected === 'string' && fromCollected.trim()) return fromCollected.trim();
    if (typeof fromCollected === 'number') return String(fromCollected);
    const field = formRequest.fields.find((f) => f.name === name);
    if (typeof field?.default === 'string' && field.default.trim()) return field.default.trim();
    if (typeof field?.default === 'number') return String(field.default);
    return '';
  };
  const amountRaw = fieldDefault('target_amount');
  const yearRaw = fieldDefault('fiscal_year');
  const quarterRaw = fieldDefault('fiscal_quarter');
  const targetAmount = amountRaw ? Number(amountRaw.replace(/,/g, '')) : undefined;
  const fiscalYear = yearRaw ? parseInt(yearRaw, 10) : undefined;
  const fiscalQuarter = quarterRaw ? parseInt(quarterRaw, 10) : undefined;
  return {
    ...(targetAmount && !Number.isNaN(targetAmount) ? { targetAmount } : {}),
    ...(fiscalYear && !Number.isNaN(fiscalYear) ? { fiscalYear } : {}),
    ...(fiscalQuarter && !Number.isNaN(fiscalQuarter) ? { fiscalQuarter } : {}),
  };
}

/** Sales target self-service create via form_catalog → generative quota modal. */
export function isSalesTargetFormCatalogRequest(formRequest: FormRequest): boolean {
  const entity = (formRequest.entity || formRequest.entityType || '').trim().toLowerCase();
  const action = (formRequest.action || '').trim().toLowerCase();
  const mode = (formRequest.mode || '').trim().toLowerCase();
  if (entity !== 'sales_target_self') return false;
  return mode !== 'edit' && action !== 'update';
}

/** Map form_catalog fields into EmailTemplateCreateModal prefill. */
export function extractEmailTemplatePrefillFromFormRequest(
  formRequest: FormRequest
): EmailTemplateCreatePrefill {
  const collected = formRequest.continuation?.collected_fields ?? {};
  const fieldDefault = (name: string): string => {
    const fromCollected = collected[name];
    if (typeof fromCollected === 'string' && fromCollected.trim()) return fromCollected.trim();
    const field = formRequest.fields.find((f) => f.name === name);
    if (typeof field?.default === 'string' && field.default.trim()) return field.default.trim();
    return '';
  };

  const name = fieldDefault('template_name') || fieldDefault('name');
  const subject = fieldDefault('subject');
  const description = fieldDefault('description');
  const bodyContent = fieldDefault('body') || fieldDefault('body_content');

  return {
    ...(name ? { name } : {}),
    ...(subject ? { subject } : {}),
    ...(description ? { description } : {}),
    ...(bodyContent ? { bodyContent } : {}),
  };
}
